'use client';

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Container,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { type FC, useEffect, useRef } from 'react';

import { AbgTypography, ChevronRightIcon } from '@abg/kit-base';

import { containerStyles, iconStyles } from './Breadcrumbs.styling';
import { type BreadcrumbsClientProps } from './breadcrumbs.types';
import {
  ELLIPSIS,
  MIN_ITEMS_FOR_MOBILE_COLLAPSE,
  OFFSET,
  PARENT_PAGE_OFFSET,
} from './constants';

export const BreadcrumbsContent: FC<BreadcrumbsClientProps> = ({
  breadcrumbList,
  aemAttributes,
}) => {
  const containerReference = useRef<HTMLDivElement | null>(null);
  const activeItemReference = useRef<HTMLDivElement | null>(null);
  const pathName = usePathname();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const displayedBreadcrumbs =
    isMobile && breadcrumbList.length >= MIN_ITEMS_FOR_MOBILE_COLLAPSE
      ? [
          breadcrumbList[0],
          {
            title: ELLIPSIS,
            link: breadcrumbList.at(-PARENT_PAGE_OFFSET)?.link ?? '',
          },
          breadcrumbList.at(-1)!,
        ]
      : breadcrumbList;

  useEffect(() => {
    if (containerReference.current && activeItemReference.current) {
      containerReference.current.scrollLeft =
        activeItemReference.current.offsetLeft - OFFSET;
    }
  }, []);

  const mobileIndices = [
    0,
    breadcrumbList.length - PARENT_PAGE_OFFSET,
    breadcrumbList.length - 1,
  ];

  return (
    <Container
      ref={containerReference}
      sx={containerStyles}
      {...aemAttributes}
      data-aue-type="component"
      data-testid="breadcrumbs-wrapper"
    >
      <MuiBreadcrumbs
        separator={<ChevronRightIcon sx={iconStyles} />}
        aria-label="breadcrumb"
        itemScope
        itemType="https://schema.org/BreadcrumbList"
      >
        {displayedBreadcrumbs.map((item, index) => {
          if (!item) return null;

          // Calculate the actual position in the original breadcrumb list
          const actualIndex =
            isMobile &&
            displayedBreadcrumbs.length === MIN_ITEMS_FOR_MOBILE_COLLAPSE
              ? mobileIndices[index]
              : index;

          return (
            <Box
              key={`${item.title}-${actualIndex}`}
              ref={
                index == displayedBreadcrumbs.length - 1
                  ? activeItemReference
                  : null
              }
              itemProp="itemListElement"
              itemScope
              itemType="https://schema.org/ListItem"
            >
              {pathName == item.link ? (
                <>
                  <AbgTypography
                    itemProp="name"
                    component="span"
                    data-testid="breadcrumb_current"
                    variant="actionSmallMedium"
                  >
                    {item.title}
                  </AbgTypography>
                  <meta itemProp="position" content={String(actualIndex)} />
                </>
              ) : (
                <>
                  <Link
                    prefetch={true}
                    href={item.link}
                    data-testid="breadcrumb_segment"
                    itemProp="item"
                  >
                    <AbgTypography
                      itemProp="name"
                      component="span"
                      variant="actionSmallRegular"
                    >
                      {item.title}
                    </AbgTypography>
                  </Link>
                  <meta itemProp="position" content={String(actualIndex)} />
                </>
              )}
            </Box>
          );
        })}
      </MuiBreadcrumbs>
    </Container>
  );
};
